import React, { useState } from 'react';
import { Provider } from '../types';
import {
  Link2,
  Unlink,
  X,
  AlertCircle,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

interface LinkProviderModalProps {
  user: any;
  currentProvider: Provider | null;
  providers: Provider[];
  existingUsers: any[];
  onClose: () => void;
  onSaved: () => void;
}

export const LinkProviderModal: React.FC<LinkProviderModalProps> = ({
  user,
  currentProvider,
  providers,
  existingUsers,
  onClose,
  onSaved
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState<string>(currentProvider?.id || '');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if selected provider is already linked to another user
  const targetProvider = providers.find((p) => p.id === selectedProviderId);
  const otherLinkedUser = targetProvider && targetProvider.user_id && targetProvider.user_id !== user.id
    ? existingUsers.find((u) => u.id === targetProvider.user_id) || { id: targetProvider.user_id, name: targetProvider.user_id, email: '' }
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (otherLinkedUser && !confirmReplace) {
      setError(`Confirmation required: Provider is already linked to ${otherLinkedUser.name || otherLinkedUser.id}. Check the confirmation box below.`);
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`/api/users/${user.id}/link-provider`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId: selectedProviderId || null,
          confirmReplace
        })
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `Server returned error status ${res.status}`);
      }

      onSaved();
      onClose();
    } catch (err: any) {
      console.error('[LinkProviderModal] Save error:', err);
      setError(err.message || 'Failed to update provider linkage.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass-panel max-w-lg w-full rounded-2xl border border-slate-800 p-6 space-y-5 my-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400">
              <Link2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Clinical Provider Linkage</h2>
              <p className="text-[11px] text-slate-400">
                {user.name} ({user.email})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-xs flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Current State Info */}
          <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl space-y-1">
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Current Status</span>
            <div className="text-slate-200 font-medium">
              {currentProvider ? (
                <div className="flex items-center space-x-2">
                  <span className="px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/20 text-[10px] font-bold text-sky-300">
                    {currentProvider.role}
                  </span>
                  <span>{currentProvider.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">({currentProvider.id})</span>
                </div>
              ) : (
                <span className="text-slate-400 italic">Operational Staff / Unlinked</span>
              )}
            </div>
          </div>

          {/* Provider Selection */}
          <div>
            <label className="block text-slate-300 font-semibold mb-1">
              Select Clinical Provider Record
            </label>
            <select
              value={selectedProviderId}
              onChange={(e) => {
                setSelectedProviderId(e.target.value);
                setConfirmReplace(false);
                setError(null);
              }}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 text-xs"
            >
              <option value="">Operational Staff / Unlink Provider</option>
              {providers.map((p) => {
                const linkedUser = existingUsers.find(
                  (u) => u.id !== user.id && (u.id === p.user_id || u.providerId === p.id)
                );
                return (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.role}) {linkedUser ? `— [Currently linked to ${linkedUser.name}]` : ''}
                  </option>
                );
              })}
            </select>
            <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
              Preserves provider's clinical contact and eSignature email unchanged. Linking only associates this user's Google login with clinical charting and caseloads.
            </p>
          </div>

          {/* Warning if already linked to another user */}
          {otherLinkedUser && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-2">
              <div className="flex items-start space-x-2 text-amber-300 text-xs font-semibold">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>
                  Provider "{targetProvider?.name}" is currently linked to user "{otherLinkedUser.name}" ({otherLinkedUser.email}).
                </span>
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                Saving will reassign this provider profile to <strong>{user.name}</strong> and unlink it from <strong>{otherLinkedUser.name}</strong>.
              </p>
              <label className="flex items-center space-x-2 text-xs text-amber-200 cursor-pointer pt-1 border-t border-amber-500/20">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                  className="rounded border-amber-500 text-amber-500 focus:ring-0"
                />
                <span className="font-semibold">I confirm reassigning this provider to {user.name}</span>
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-5 py-2 rounded-lg text-xs font-semibold shadow-md transition-all flex items-center space-x-1.5 disabled:opacity-50 ${
                !selectedProviderId
                  ? 'bg-rose-900/60 hover:bg-rose-900 text-rose-200 border border-rose-700/60'
                  : 'bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white shadow-sky-900/30'
              }`}
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : !selectedProviderId ? (
                <>
                  <Unlink className="w-3.5 h-3.5" />
                  <span>Unlink Provider</span>
                </>
              ) : (
                <>
                  <Link2 className="w-3.5 h-3.5" />
                  <span>Save Linkage</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
